import { Amplify } from '@aws-amplify/core';
import { RoutePath } from '../RoutePath';
import { BASE_PATH_PREFIX } from '../utils/env.utils';
import { getSignOutUrl } from '../utils/hybrid-console/index';
import { config } from './app-config';

const addQueryParams = (url: string, newParams: Record<string, string>) => {
  const urlObj = new URL(url);
  Object.entries(newParams).forEach(([key, value]) => {
    urlObj.searchParams.set(key, value);
  });
  return urlObj.toString();
};

/**
 * Create Gandalf signIn interceptor
 *
 * @param clientId Jam's Gandalf client Id
 * @param issuer Gandalf cognito auth domain
 * @param login Gandalf login domain
 * @param logout Gandalf logout domain
 * @param additionalParameters
 */
const createUrlInterceptor =
  (
    issuer: string,
    login: string,
    logout: string,
    clientId: string,
    additionalParameters: Record<string, string> = {}
  ) =>
  (url: string) => {
    const SELF = '_self';
    const loginRequestUrl = `https://${issuer}/oauth2/authorize`;
    const logoutRequestUrl = `https://${issuer}/logout`;
    let windowProxy;

    if (login && url.startsWith(loginRequestUrl)) {
      const signInUrl = url.replace(loginRequestUrl, `https://${login}/login`);
      windowProxy = window.open(addQueryParams(signInUrl, additionalParameters), SELF, 'noopener,noreferrer');
    } else if (logout && url.startsWith(logoutRequestUrl)) {
      // Configure popup window dimensions and position.
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      // Open additional logout URL in popup with specific settings. This window will open the Gandalf managed
      // Logout endpoint as known as global logout url as well. This is required to address the security issue
      // mentioned in the ticket https://t.corp.amazon.com/V1764289297
      // url format given by Gandalf https://us-east-1.auth.skillbuilder.aws/logout?client_id=<yourClientId>
      // &response_type=code&logout_uri=https://login.us-east-1.auth.skillbuilder.aws/logout?client_id=<yourClientId>
      window.open(
        `https://${logout}/logout?client_id=${clientId}&response_type=code&logout_uri=https://${login}/logout?client_id=${clientId}`,
        'LogoutWindow',
        `width=${width},height=${height},left=${left},top=${top},popup=1,scrollbars=1,resizable=1`
      );

      const signOutUrl = url.replace(logoutRequestUrl, `https://${logout}/logout`);
      windowProxy = window.open(signOutUrl, SELF, 'noopener,noreferrer');
    } else {
      windowProxy = window.open(url, SELF, 'noopener,noreferrer');
    }
    return windowProxy ? Promise.resolve(windowProxy) : Promise.reject();
  };

const getBaseUrl = (basePathPrefix: string) => {
  let port: string | null = window.location.port;
  if (port === '443' || port === '80') {
    port = null;
  }

  // i.e. https://localhost:4201/v2
  return `${window.location.protocol}//${window.location.hostname}${port ? ':' + port : ''}${basePathPrefix}`;
};

export const getSignInUrl = (basePathPrefix: string): string => {
  return `${getBaseUrl(basePathPrefix)}${RoutePath.OAUTH2}`;
};

const defaultConfig = {
  Auth: {
    region: config.cognito.gandalfRegion,
    userPoolId: config.cognito.userPoolId,
    userPoolWebClientId: config.cognito.clientId,
    oauth: {
      domain: config.cognito.appWebDomain,
      scope: ['openid'],
      redirectSignIn: getSignInUrl(BASE_PATH_PREFIX),
      redirectSignOut: getSignOutUrl(BASE_PATH_PREFIX),
      responseType: 'code',
      urlOpener: createUrlInterceptor(
        config.cognito.appWebDomain,
        config.cognito.gandalfLogin,
        config.cognito.gandalfLogout,
        config.cognito.clientId
      ),
    },
  },
};

/**
 * Used to set additional query parameters on Gandalf sign-in.
 * See configureAmplify function below for more details.
 */
const getCustomConfig = (additionalParameters: Record<string, string>) => {
  return {
    Auth: {
      ...defaultConfig.Auth,
      oauth: {
        ...defaultConfig.Auth.oauth,
        urlOpener: createUrlInterceptor(
          config.cognito.appWebDomain,
          config.cognito.gandalfLogin,
          config.cognito.gandalfLogout,
          config.cognito.clientId,
          additionalParameters
        ),
      },
    },
  };
};

/**
 * Sets up Amplify to leverage Gandalf authentication.
 * Calling this function without parameters will default to the basic login/logout behavior.
 * Sometimes additional URL parameters are required to use special Gandalf features
 * (email verification for example).
 *
 * Note that most parts of the Jam system depend on the default sign-in configuration,
 * so if this function is used to configure a special sign-in, it should be re-configured back
 * to the default after the operation is complete.
 *
 * @param additionalParameters (optional) - extra query parameters to append to Gandalf's sign-in URL
 */
export const configureAmplify = (additionalParameters?: Record<string, string>) => {
  if (additionalParameters) {
    Amplify.configure(getCustomConfig(additionalParameters));
  } else {
    Amplify.configure(defaultConfig);
  }
};

export default defaultConfig;
